import { Drawer } from 'vaul';
import * as Tabs from '@radix-ui/react-tabs';
import { Link } from '@tanstack/react-router';
import { UserButton } from '@clerk/clerk-react';
import { UserCircleIcon, SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '@/store/uiStore';
import { useTheme } from '@/hooks/useTheme';
import { ChatsList } from '@/components/chat/ChatsList';
import { BrowseNav } from './BrowseNav';
import { WakeWordToggle } from './WakeWordController';

const AUTH_DEV_BYPASS = import.meta.env.VITE_AUTH_DEV_BYPASS === 'true';

/** Shared drawer body: tabs + footer. Used by both mobile drawer and desktop rail. */
export function DrawerBody() {
  const { navDrawerTab, setNavDrawerTab, closeNavDrawer } = useUIStore();
  const { theme, setTheme } = useTheme();

  const cycleTheme = () => setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light');
  const ThemeIcon = theme === 'light' ? SunIcon : theme === 'dark' ? MoonIcon : ComputerDesktopIcon;

  return (
    <div className="flex h-full flex-col surface-low">
      <div className="px-4 pt-4 pb-2">
        <h1 className="text-base font-display font-bold tracking-tight text-text-primary">Second Brain AI</h1>
      </div>

      <Tabs.Root
        value={navDrawerTab}
        onValueChange={(v) => setNavDrawerTab(v as 'chats' | 'browse')}
        className="flex min-h-0 flex-1 flex-col"
      >
        <Tabs.List className="mx-3 mb-1 grid grid-cols-2 gap-1 rounded-xl bg-surface-container-high/60 p-1" aria-label="Drawer sections">
          <Tabs.Trigger
            value="chats"
            className="rounded-lg px-3 py-1.5 text-[13px] font-medium text-text-secondary transition-colors data-[state=active]:bg-surface-container-high data-[state=active]:text-text-primary data-[state=active]:shadow-sm"
          >
            Chats
          </Tabs.Trigger>
          <Tabs.Trigger
            value="browse"
            className="rounded-lg px-3 py-1.5 text-[13px] font-medium text-text-secondary transition-colors data-[state=active]:bg-surface-container-high data-[state=active]:text-text-primary data-[state=active]:shadow-sm"
          >
            Browse
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="chats" className="min-h-0 flex-1 focus:outline-none">
          <ChatsList onNavigate={closeNavDrawer} />
        </Tabs.Content>
        <Tabs.Content value="browse" className="min-h-0 flex-1 focus:outline-none">
          <BrowseNav onNavigate={closeNavDrawer} />
        </Tabs.Content>
      </Tabs.Root>

      <WakeWordToggle />

      {/* Footer: account + theme */}
      <div className="flex items-center justify-between gap-2 border-t border-outline-variant/20 px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          {AUTH_DEV_BYPASS ? (
            <UserCircleIcon className="h-7 w-7 text-text-secondary" />
          ) : (
            <UserButton afterSignOutUrl="/" appearance={{ elements: { avatarBox: 'w-7 h-7' } }} />
          )}
          <Link
            to="/profile"
            onClick={closeNavDrawer}
            className="text-[13px] font-medium text-text-secondary transition-colors hover:text-text-primary"
          >
            Account
          </Link>
        </div>
        <button
          onClick={cycleTheme}
          aria-label={`Theme: ${theme}`}
          className="rounded-lg p-2 text-text-secondary transition-colors hover:bg-surface-container-high hover:text-text-primary"
        >
          <ThemeIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/** Desktop persistent rail — collapsible/slidable via sidebarCollapsed. */
export function NavRail() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  return (
    <aside
      className={`hidden flex-shrink-0 overflow-hidden border-outline-variant/30 transition-[width] duration-300 ease-in-out md:block ${
        collapsed ? 'w-0 border-r-0' : 'w-72 border-r'
      }`}
      aria-label="Navigation"
      aria-hidden={collapsed}
    >
      {/* Fixed inner width so content doesn't reflow while the rail slides. */}
      <div className="h-full w-72">
        <DrawerBody />
      </div>
    </aside>
  );
}

/** Mobile overlay drawer (vaul, left direction, drag-to-close). */
export function NavDrawer() {
  const { isNavDrawerOpen, openNavDrawer, closeNavDrawer } = useUIStore();
  return (
    <Drawer.Root
      direction="left"
      open={isNavDrawerOpen}
      onOpenChange={(o) => (o ? openNavDrawer() : closeNavDrawer())}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <Drawer.Content
          className="fixed inset-y-0 left-0 z-50 flex w-[85vw] max-w-xs outline-none"
          aria-label="Navigation drawer"
        >
          <div className="flex h-full w-full flex-col surface-low safe-area-inset-top">
            <DrawerBody />
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
